import { FC, ReactNode } from 'react';
import { Button, Col, Divider, Flex, Popconfirm } from 'antd';
import { TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type {
    WorkspaceResourceAdvancedFilterSetter,
    WorkspaceResourceAdvancedFiltersSetter,
    WorkspaceResourceFilterDefinitionSetter,
    WorkspaceResourceFilterSetter,
    WorkspaceResourceFilterUpdateOptions,
} from '@/hooks/useWorkspaceResourceFilters';
import { SavedFilterResourceType } from '@/services';
import { FilterRowEmptyState, FilterRowSurface, getAdvancedFilterCounts, SmallTooltip } from '@/shared';
import type { AdvancedFilterDefinition } from '@/shared';
import type { ListType } from '@/types';

import FilterDropdown, { type FilterableColumn, type ListFilterRule } from '../01-FilterDropdown';
import AppliedFilters from '../02-AppliedFilters';
import SavedFiltersDropdown from '../SavedFiltersDropdown';

interface WorkspaceResourceFilterControlsProps {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    advancedFilters?: AdvancedFilterDefinition<ListFilterRule>[];
    columns: FilterableColumn[];
    docsLabel: string;
    emptyColumnsDescription: string;
    emptyHint: string;
    filterableResourceLabel: string;
    filters: ListFilterRule[];
    horizontalPadding?: number | string;
    leading?: ReactNode;
    onChange: WorkspaceResourceFilterSetter;
    onAdvancedChange?: WorkspaceResourceAdvancedFilterSetter;
    onAdvancedFiltersChange?: WorkspaceResourceAdvancedFiltersSetter;
    onDefinitionChange?: WorkspaceResourceFilterDefinitionSetter;
    resourceLabel: string;
    showEmptyHint?: boolean;
    trailing?: ReactNode;
}

interface WorkspaceResourceFiltersRowProps extends Omit<WorkspaceResourceFilterControlsProps, 'trailing'> {
    canManageSharedFilters: boolean;
    compatibleResourceLabel: string;
    resourceId?: string;
    resourceScopedLabel: string;
    resourceType: SavedFilterResourceType;
    onDefinitionChange?: WorkspaceResourceFilterDefinitionSetter;
    targetKind?: ListType;
    workspaceScopeDescription: string;
    workspaceScopeTitle: string;
}

const WorkspaceResourceFilterControls: FC<WorkspaceResourceFilterControlsProps> = ({
    advancedFilter,
    advancedFilters = [],
    columns,
    docsLabel,
    emptyColumnsDescription,
    emptyHint,
    filterableResourceLabel,
    filters,
    horizontalPadding = '1.5rem',
    leading,
    onChange,
    onAdvancedChange,
    onAdvancedFiltersChange,
    onDefinitionChange,
    resourceLabel,
    showEmptyHint = true,
    trailing,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const resolvedAdvancedFilters = advancedFilters.length ? advancedFilters : advancedFilter ? [advancedFilter] : [];
    const advancedConditionCount = resolvedAdvancedFilters.reduce(
        (count, filter) => count + getAdvancedFilterCounts(filter).conditions,
        0,
    );
    const hasFilters = filters.length > 0 || advancedConditionCount > 0;

    const handleSetFilters = (nextFilters: ListFilterRule[] | null, options?: WorkspaceResourceFilterUpdateOptions) => {
        onChange(nextFilters?.length ? nextFilters : null, options);
    };

    return (
        <FilterRowSurface horizontalPadding={horizontalPadding}>
            <Col flex='none'>
                <Flex align='center'>
                    {leading}
                    <FilterDropdown
                        columns={columns}
                        docsLabel={docsLabel}
                        emptyColumnsDescription={emptyColumnsDescription}
                        filterableResourceLabel={filterableResourceLabel}
                        filters={filters}
                        onAdvancedFilterChange={(next) => {
                            if (!next) return;
                            if (onAdvancedFiltersChange) {
                                onAdvancedFiltersChange([...resolvedAdvancedFilters, next]);
                            } else {
                                onAdvancedChange?.(next);
                            }
                        }}
                        setFilters={handleSetFilters}
                    />
                    {(hasFilters || showEmptyHint) && <Divider type='vertical' />}
                </Flex>
            </Col>
            {!hasFilters ? (
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <FilterRowEmptyState hint={showEmptyHint ? emptyHint : undefined} />
                </Col>
            ) : (
                <Col flex='auto' style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                    <AppliedFilters
                        advancedFilters={resolvedAdvancedFilters}
                        columns={columns}
                        docsLabel={docsLabel}
                        filters={filters}
                        onAdvancedFiltersChange={
                            onAdvancedFiltersChange ??
                            (onAdvancedChange ? (definitions) => onAdvancedChange(definitions[0] ?? null) : undefined)
                        }
                        setFilters={handleSetFilters}
                    />
                </Col>
            )}
            {hasFilters && (
                <Col flex='none'>
                    <Popconfirm
                        description={`This will remove every filter currently applied to ${resourceLabel}.`}
                        okButtonProps={{ danger: true }}
                        okText='Clear Filters'
                        placement='topRight'
                        title='Clear all filters?'
                        onConfirm={() => {
                            if (onDefinitionChange) {
                                onDefinitionChange(null, null);
                            } else {
                                handleSetFilters(null);
                                onAdvancedFiltersChange?.([]);
                                onAdvancedChange?.(null);
                            }
                        }}
                    >
                        <span>
                            <SmallTooltip padding='0 0.5rem' placement='left' title='Clear All Filters'>
                                <Button size='small' type='dashed'>
                                    <TbX style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />
                                </Button>
                            </SmallTooltip>
                        </span>
                    </Popconfirm>
                    {trailing ? <Divider type='vertical' /> : null}
                </Col>
            )}
            {trailing ? <Col flex='none'>{trailing}</Col> : null}
        </FilterRowSurface>
    );
};

const WorkspaceResourceFiltersRow: FC<WorkspaceResourceFiltersRowProps> = ({
    advancedFilter,
    advancedFilters,
    canManageSharedFilters,
    columns,
    compatibleResourceLabel,
    docsLabel,
    emptyColumnsDescription,
    emptyHint,
    filterableResourceLabel,
    filters,
    horizontalPadding,
    leading,
    onChange,
    onAdvancedChange,
    onAdvancedFiltersChange,
    onDefinitionChange,
    resourceId,
    resourceLabel,
    resourceScopedLabel,
    resourceType,
    showEmptyHint,
    targetKind,
    workspaceScopeDescription,
    workspaceScopeTitle,
}): JSX.Element => (
    <WorkspaceResourceFilterControls
        advancedFilter={advancedFilter}
        advancedFilters={advancedFilters}
        columns={columns}
        docsLabel={docsLabel}
        emptyColumnsDescription={emptyColumnsDescription}
        emptyHint={emptyHint}
        filterableResourceLabel={filterableResourceLabel}
        filters={filters}
        horizontalPadding={horizontalPadding}
        leading={leading}
        onChange={onChange}
        onAdvancedChange={onAdvancedChange}
        onAdvancedFiltersChange={onAdvancedFiltersChange}
        onDefinitionChange={onDefinitionChange}
        resourceLabel={resourceLabel}
        showEmptyHint={showEmptyHint}
        trailing={
            <SavedFiltersDropdown
                advancedFilter={advancedFilters?.length === 1 ? advancedFilters[0] : advancedFilter}
                canManageSharedFilters={canManageSharedFilters}
                columns={columns}
                compatibleResourceLabel={compatibleResourceLabel}
                docsLabel={docsLabel}
                filters={filters}
                resourceId={resourceId}
                resourceLabel={resourceLabel}
                resourceScopedLabel={resourceScopedLabel}
                resourceType={resourceType}
                setFilterDefinition={onDefinitionChange}
                setFilters={(nextFilters, options) => onChange(nextFilters?.length ? nextFilters : null, options)}
                targetKind={targetKind}
                workspaceScopeDescription={workspaceScopeDescription}
                workspaceScopeTitle={workspaceScopeTitle}
            />
        }
    />
);

export { WorkspaceResourceFilterControls };
export default WorkspaceResourceFiltersRow;
